import React from "react";
import { getDestinations, getDestination } from "lib/api";
import PropTypes from "prop-types";
import LayoutDestination from "components/layouts/LayoutDestination";
import Meta from "solar/components/Meta";
import { Media } from "solar/components/Media";
import Title from "components/shared/stays/Title";
import GalleryTwoUp from "components/shared/stays/GalleryTwoUp";
import SurfConditions from "components/shared/stays/SurfConditions";
import Navbar from "components/shared/stays/Navbar";
import GallerySmall from "components/destination/GallerySmall";
import PaginateTrips from "components/shared/stays/PaginateTrips";
import MapSmall from "components/shared/stays/MapSmall";
import MapLarge from "components/shared/stays/MapLarge";
import Story from "components/destination/Story";
import ColumnTwoUp from "components/shared/stays/ColumnTwoUp";
import ColumnTwoUpDetails from "components/shared/stays/ColumnTwoUpDetails";
import Divider from "components/shared/stays/Divider";
import BreadCrumb from "components/destination/BreadCrumb";
import CountryList from "components/destination/CountryList";
import RegionList from "components/destination/RegionList";

export default function Destination({ destination }) {
  const maxGalleryImages = 20;

  // append tripName key to images
  const tripImages = destination?.trips.map((trip) =>
    trip.trip_media_images.map((image) => ({ ...image, tripName: trip.name }))
  );

  // sort, limit, and reassign position to images and convert id to string
  const galleryImages = tripImages
    .flat()
    .sort((a, b) => a.position - b.position)
    .slice(0, maxGalleryImages)
    .map((elem, idx) => ({
      ...elem,
      position: idx + 1,
      id: elem.id.toString(),
    }));

  const tripCountries = destination?.trips.reduce((acc, trip) => {
    acc[trip.country] = { slug: trip.country_destination_slug };
    return acc;
  }, {});

  const tripRegions = destination?.trips.reduce((acc, trip) => {
    acc[trip.region] = {
      image: trip.trip_media_images[0]?.file,
      slug: trip.region_destination_slug,
    };
    return acc;
  }, {});

  return (
    <>
      <Meta
        title={`Surf ${destination.name}`}
        description={`A hand-picked collection of the best surf trips in ${destination.name} — focused on authentic experiences found nowhere else.`}
        path={`/destinations/${destination.slug}`}
        imageOgUrl={galleryImages[0]?.file.url}
        imageTwitterUrl={galleryImages[0]?.file.url}
        imageType={galleryImages[0]?.file.content_type}
      />
      {destination && (
        <>
          <Navbar
            parentName={
              destination.breadcrumb.length > 0
                ? destination.breadcrumb[destination.breadcrumb.length - 1].name
                : "All stays"
            }
            parentPath={
              destination.breadcrumb.length > 0
                ? `/destinations/${
                    destination.breadcrumb[destination.breadcrumb.length - 1]
                      .slug
                  }`
                : "/stays"
            }
            smallPrimary="search"
            smallSecondary="menu"
            bigPrimary="logo"
            bigSecondary="search"
            bigTertiary="links-and-menu"
            bigFluid
          />
          <ColumnTwoUp>
            <Media lessThan="md">
              {galleryImages.length > 0 && (
                <GallerySmall photos={galleryImages} />
              )}
            </Media>
            <ColumnTwoUpDetails>
              {destination.breadcrumb.length > 0 && (
                <BreadCrumb
                  links={destination.breadcrumb}
                  currentDestination={destination.name}
                />
              )}

              <Story name={destination.name} description={destination.story} />

              <Media greaterThanOrEqual="md">
                {galleryImages.length > 0 && (
                  <GalleryTwoUp
                    photos={galleryImages}
                    photoAlt={`${destination.name} photos`}
                    showButton
                    buttonText={`Show ${galleryImages.length} photos`}
                    prioritizeFirstPhoto
                  />
                )}
              </Media>

              <Media greaterThanOrEqual="lg">
                <Divider />
              </Media>

              {destination.trips.length > 0 && (
                <>
                  <Media lessThan="lg">
                    <MapSmall trips={destination.trips} />
                    <Divider />
                  </Media>
                  {destination.page_type === "world_area" && (
                    <>
                      <CountryList countries={tripCountries} />
                      <Divider />
                    </>
                  )}
                  {destination.page_type === "country" && (
                    <>
                      <RegionList regions={tripRegions} />
                      <Divider />
                    </>
                  )}
                  {destination.page_type === "region" &&
                    destination?.trips[0].region_seasons && (
                      <>
                        <SurfConditions
                          season={destination.trips[0].region_seasons}
                          oneLetter
                        />
                        <Divider />
                      </>
                    )}

                  <Title tag="h2" size={4}>
                    {`Stays in ${destination.name}`}
                  </Title>

                  <PaginateTrips
                    trips={destination.trips}
                    initialTripCount={10}
                  />
                </>
              )}
            </ColumnTwoUpDetails>
            <MapLarge key={destination.slug} trips={destination.trips} />
          </ColumnTwoUp>
        </>
      )}
    </>
  );
}

export async function getStaticPaths() {
  const response = await getDestinations();
  if (response.errorMsg) {
    return { paths: [], fallback: "blocking" };
  }

  const paths = response.map((destination) => ({
    params: { slug: destination.slug },
  }));
  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const response = await getDestination(params.slug);
  if (response.errorMsg) {
    return { notFound: true };
  }
  return {
    props: { destination: response },
    revalidate: 86400,
  };
}

Destination.propTypes = {
  destination: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    story: PropTypes.string,
    page_type: PropTypes.string,
    trips: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        region_seasons: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    breadcrumb: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
      })
    ),
  }),
};

Destination.Layout = LayoutDestination;
